export interface TaprootOutputFields {
  compactSize: string
  witnessVersion: string
  pushLength: string
  outputKey: string
}

export type TaprootWitnessRole =
  | 'key-path-signature'
  | 'script-path-stack-argument'
  | 'tapscript'
  | 'control-block'
  | 'annex'

export interface TaprootWitnessField {
  compactSize: string
  payload: string
  payloadLength: number
  role: TaprootWitnessRole
}

interface SerializedField {
  compactSize: string
  payload: string
  payloadLength: number
}

const hexPattern = /^[0-9a-fA-F]*$/

function parseSerializedField(serialized: string): SerializedField | undefined {
  if (!hexPattern.test(serialized) || serialized.length < 2 || serialized.length % 2 !== 0) {
    return undefined
  }

  const firstByte = Number.parseInt(serialized.slice(0, 2), 16)
  const compactSizeBytes = firstByte < 0xfd ? 1 : firstByte === 0xfd ? 3 : firstByte === 0xfe ? 5 : 9

  if (serialized.length < compactSizeBytes * 2) {
    return undefined
  }

  let payloadLength = firstByte
  if (compactSizeBytes > 1) {
    payloadLength = 0
    for (let index = 1; index < compactSizeBytes; index += 1) {
      const byte = Number.parseInt(serialized.slice(index * 2, index * 2 + 2), 16)
      payloadLength += byte * 256 ** (index - 1)
      if (!Number.isSafeInteger(payloadLength)) {
        return undefined
      }
    }
  }

  const isCanonical =
    compactSizeBytes === 1 ||
    (compactSizeBytes === 3 && payloadLength >= 0xfd) ||
    (compactSizeBytes === 5 && payloadLength > 0xffff) ||
    (compactSizeBytes === 9 && payloadLength > 0xffffffff)
  const compactSize = serialized.slice(0, compactSizeBytes * 2)
  const payload = serialized.slice(compactSizeBytes * 2)

  if (!isCanonical || payload.length !== payloadLength * 2) {
    return undefined
  }

  return { compactSize, payload, payloadLength }
}

export function parseTaprootOutputFields(serializedScript: string): TaprootOutputFields | undefined {
  const field = parseSerializedField(serializedScript)
  if (!field || field.payloadLength !== 34 || field.payload.slice(0, 4).toLowerCase() !== '5120') {
    return undefined
  }

  return {
    compactSize: field.compactSize,
    witnessVersion: field.payload.slice(0, 2),
    pushLength: field.payload.slice(2, 4),
    outputKey: field.payload.slice(4)
  }
}

function isTapscriptControlBlock(payload: string): boolean {
  if (payload.length < 2) {
    return false
  }

  const byteLength = payload.length / 2
  const leafVersion = Number.parseInt(payload.slice(0, 2), 16) & 0xfe
  return leafVersion === 0xc0 && byteLength >= 33 && byteLength <= 33 + 32 * 128 && (byteLength - 33) % 32 === 0
}

export function classifyTaprootWitness(serializedItems: string[]): TaprootWitnessField[] | undefined {
  const fields = serializedItems.map(parseSerializedField)
  if (fields.some((field) => !field)) {
    return undefined
  }

  const parsedFields = fields as SerializedField[]
  const annexIndex =
    parsedFields.length >= 2 && parsedFields[parsedFields.length - 1].payload.slice(0, 2).toLowerCase() === '50'
      ? parsedFields.length - 1
      : undefined
  const spendFields = annexIndex === undefined ? parsedFields : parsedFields.slice(0, annexIndex)
  let roles: TaprootWitnessRole[]

  if (spendFields.length === 1 && [64, 65].includes(spendFields[0].payloadLength)) {
    roles = ['key-path-signature']
  } else if (spendFields.length >= 2 && isTapscriptControlBlock(spendFields[spendFields.length - 1].payload)) {
    roles = spendFields.map((_, index) => {
      if (index === spendFields.length - 1) {
        return 'control-block'
      }
      if (index === spendFields.length - 2) {
        return 'tapscript'
      }
      return 'script-path-stack-argument'
    })
  } else {
    return undefined
  }

  if (annexIndex !== undefined) {
    roles.push('annex')
  }

  return parsedFields.map((field) => ({ ...field, role: roles.shift() as TaprootWitnessRole }))
}
